import { DocumentStatus } from "@types";
import { useEffect, useMemo, useRef, useState } from "react";

import {
  useGetDevice,
  useGetDocuments,
  useGetDocumentsTemplates,
  useGetFile,
  useGetTasks,
  useGetUser,
  usePatchDocumentVersionMutation,
} from "@hooks";
import { Document, DocumentVersion } from "@models";
import {
  buildDownloadFileName,
  canDocHaveMultipleInstances,
  DEFAULT_ACCEPTED_FILE_TYPES,
  findInstancesOfTemplateKey,
  getDocumentNavigationUrl,
  getDocVersionStatus,
} from "@utils";
import { getOrgFromUser } from "src/utils/user";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ConfirmationModal } from "src/components/ConfirmationModal";
import { DocumentContextWarnings } from "src/components/DocViewer/components/DocumentContextWarnings";
import { DocViewerTopBar } from "src/components/DocViewer/components/docViewerTopBar/DocViewerTopBar";
import UploadModal, {
  UploadModalType,
} from "src/components/DocViewer/components/fileDocViewer/UploadModal";
import { isVersionBeforeFirstRevision } from "src/components/DocViewer/components/helpers/document-version";
import {
  getArchiveFileName,
  handleFileDownload,
} from "src/components/DocViewer/components/helpers/fileDownload";
import { useIsDiffMode } from "src/components/DocViewer/components/hooks/useIsDiffMode";
import useOutstandingAction from "src/components/DocViewer/components/hooks/useOutstandingAction";
import { DocumentViewerSkeleton } from "src/components/Skeletons";
import { useSnackbar } from "src/components/Snackbar";
import {
  useCreateNewUserUploadDocumentVersion,
  useCreateUserUploadDocument,
} from "src/hooks/useUserUpload";
import { exportUploadedDocumentArchive } from "src/services/backend";
import DocInfoDrawer from "../docInfoDrawer/DocInfoDrawer";
import { FilePreview } from "./FilePreview";
import FileUpload from "./FileUpload";

/**
 * Component for viewing a document from an uploaded file and editing it.
 * States:
 *  - DRAFT: Not really used for these docs. Basically it likely only happens if no document is uploaded yet but a version was created.
 *  - UNAPPROVED: readyForApproval is true, but revision still null.
 *  file exists in fildId, which is rendered here.
 *  Will have docApprovers.
 *  - LIVE: The document is the most current revision.
 *  - ARCHIVED: The document is an older revision.
 */
type FileDocViewerProps = {
  deviceId: string;
  docWithAllVersions?: Document;
  selectedDocVersion?: DocumentVersion;
};

export const FileDocViewer = (props: FileDocViewerProps) =>
  props.docWithAllVersions?.softDeletionEvent ? (
    <DeletedFileDocViewer {...props} />
  ) : (
    <ActiveFileDocViewer {...props} />
  );

const DeletedFileDocViewer = ({
  docWithAllVersions,
  selectedDocVersion,
}: FileDocViewerProps) => {
  const { orgId = "" } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: fileBlob, isLoading: isFileLoading } = useGetFile({
    orgId,
    fileId: selectedDocVersion?.fileId || "",
  });
  const file = selectedDocVersion?.file;
  const selectVersion = (version: DocumentVersion) => {
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set("v", version.id);
    setSearchParams(nextSearchParams);
  };

  return (
    <div className="relative flex w-full flex-1 flex-col">
      {docWithAllVersions && selectedDocVersion && (
        <DocViewerTopBar
          docProps={{
            docs: [],
            selectedDoc: docWithAllVersions,
            selectedVersion: selectedDocVersion,
            hasOutstandingAction: false,
            loadingNewDocVersion: false,
            onSelectDocVersion: selectVersion,
            isDocEnabled: true,
          }}
        />
      )}
      {isFileLoading ? (
        <DocumentViewerSkeleton />
      ) : file && fileBlob?.data ? (
        <FilePreview file={file} fileBlob={fileBlob.data} />
      ) : (
        <div className="flex flex-1 items-center justify-center text-gray-500">
          This document version has no retained file.
        </div>
      )}
    </div>
  );
};

const ActiveFileDocViewer = ({
  deviceId,
  docWithAllVersions,
  selectedDocVersion,
}: FileDocViewerProps) => {
  const { orgId = "" } = useParams();
  const { isDiffMode, setIsDiffMode } = useIsDiffMode();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();

  const { data: user } = useGetUser();
  const { data: device } = useGetDevice({ orgId, deviceId });
  const { data: tasks } = useGetTasks(orgId, deviceId);
  const { data: documents } = useGetDocuments(orgId, deviceId);
  const { data: templatesConfig } = useGetDocumentsTemplates({
    orgId,
    deviceId,
  });
  const { mutateAsync: patchDocVersion } = usePatchDocumentVersionMutation();
  const { uploadFileAsDocument } = useCreateUserUploadDocument(orgId, deviceId);
  const { createNewVersion, isPending: isCreatingVersion } =
    useCreateNewUserUploadDocumentVersion(orgId, deviceId);
  const fileInput = useRef<HTMLInputElement>(null);
  const replaceFileInput = useRef<HTMLInputElement>(null);

  const navigateToDoc = (docId: string, navVersionId?: string) => {
    const url = getDocumentNavigationUrl(orgId, deviceId, docId, navVersionId);
    const separator = url.includes("?") ? "&" : "?";
    navigate(isDiffMode ? `${url}${separator}diff=true` : url);
  };

  const [showUploadModal, setShowUploadModal] = useState<
    undefined | UploadModalType
  >();

  const [showDocInfoDrawer, setShowDocInfoDrawer] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<
    "error" | "idle" | "success" | "pending"
  >("idle");
  const toggleDrawer = () => setShowDocInfoDrawer((prev) => !prev);

  const docInstances = useMemo(
    () =>
      docWithAllVersions &&
      documents &&
      canDocHaveMultipleInstances({
        document: docWithAllVersions,
        documents,
      })
        ? findInstancesOfTemplateKey(docWithAllVersions, documents)
        : [],
    [documents, docWithAllVersions]
  );

  const {
    data: fileBlob,
    isSuccess: isFileSuccess,
    isLoading: isFileLoading,
  } = useGetFile({
    orgId,
    fileId: selectedDocVersion?.fileId || "",
  });

  const {
    data: changesFileBlob,
    isSuccess: isChangesFileSuccess,
    isLoading: isChangesFileLoading,
  } = useGetFile({
    orgId,
    fileId:
      isDiffMode && selectedDocVersion?.changesFileId
        ? selectedDocVersion.changesFileId
        : "",
  });

  const outstandingAction = useOutstandingAction({
    document: docWithAllVersions,
    selectedDocVersionId: selectedDocVersion?.id || "",
    user,
    setDocInfoDrawerOpen: setShowDocInfoDrawer,
    selectedDocVersion: selectedDocVersion,
  });

  const documentStatus =
    docWithAllVersions && selectedDocVersion && user
      ? getDocVersionStatus({
          selectedDocVersion,
          document: docWithAllVersions,
          user,
          orgId,
        })
      : "DRAFT";

  // Auto-close approval drawer when document status doesn't support approvals.
  // Uploaded documents are created as drafts and can still be submitted through
  // the approval drawer.
  useEffect(() => {
    const allowedForApproval: DocumentStatus[] = [
      DocumentStatus.UNAPPROVED,
      DocumentStatus.LIVE,
      DocumentStatus.ARCHIVED,
    ];
    const isDraftUpload =
      documentStatus === DocumentStatus.DRAFT && docWithAllVersions?.userUpload;

    if (
      !isDraftUpload &&
      !allowedForApproval.includes(documentStatus as DocumentStatus)
    ) {
      setShowDocInfoDrawer(false);
    }
  }, [docWithAllVersions?.userUpload, documentStatus]);

  // If someone links `diff=true` but the current version has no changes file
  // Or if there's not changes-file because it was just deleted
  useEffect(() => {
    if (isDiffMode && selectedDocVersion && !selectedDocVersion.changesFileId) {
      setIsDiffMode(false);
    }
  }, [isDiffMode, selectedDocVersion?.id, selectedDocVersion?.changesFileId]);

  if (
    isFileLoading ||
    (isDiffMode && isChangesFileLoading) ||
    !docWithAllVersions ||
    !device ||
    !user ||
    !tasks ||
    !documents
  ) {
    return <DocumentViewerSkeleton />;
  }

  const onAbort = () => setShowDeleteConfirmation(false);

  const handleNewRecordFromFile = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (
      !canDocHaveMultipleInstances({
        document: docWithAllVersions,
        documents,
      }) ||
      !docWithAllVersions.templateKey // Just for type safety
    ) {
      return;
    }

    const document = await uploadFileAsDocument(file, {
      linkedToTemplateKey: docWithAllVersions.templateKey,
    });

    navigateToDoc(document.id, document.versions[0]?.id);
  };

  const handleReplaceFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !docWithAllVersions) return;

    const newVersion = await createNewVersion(docWithAllVersions.id, file);
    navigateToDoc(docWithAllVersions.id, newVersion.id);
    e.target.value = "";
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !docWithAllVersions) return;

    setUploadStatus("pending");
    try {
      const newVersion = await createNewVersion(docWithAllVersions.id, file);
      setUploadStatus("success");
      navigateToDoc(docWithAllVersions.id, newVersion.id);
    } catch {
      setUploadStatus("error");
      e.target.value = "";
    }
  };

  const onConfirmDeleteChangesDocument = async () => {
    if (!selectedDocVersion?.changesFileId || !docWithAllVersions) return;
    await patchDocVersion({
      orgId,
      docId: docWithAllVersions.id,
      deviceId,
      docVersionId: selectedDocVersion.id,
      data: { changesFileId: null },
    });
    setShowDeleteConfirmation(false);
    setIsDiffMode(false);
  };

  const handleBeforeSubmitApproval = async () => {
    if (!selectedDocVersion || !docWithAllVersions) return;

    await patchDocVersion({
      orgId,
      docId: docWithAllVersions.id,
      deviceId,
      docVersionId: selectedDocVersion.id,
      data: { readyForApproval: true },
    });
  };

  const activeFile = isDiffMode
    ? selectedDocVersion?.changesFile
    : selectedDocVersion?.file;
  const activeBlob = isDiffMode ? changesFileBlob?.data : fileBlob?.data;
  const isActiveFileSuccess = isDiffMode ? isChangesFileSuccess : isFileSuccess;
  const hasVersions = (docWithAllVersions?.versions.length ?? 0) > 0;

  const isAVersionBeforeFirstRevision = isVersionBeforeFirstRevision(
    selectedDocVersion,
    docWithAllVersions
  );
  const isFirstRevisionOrVersionForFirstRevision =
    selectedDocVersion?.revision === 0 || isAVersionBeforeFirstRevision;

  const downloadActiveFile = async () => {
    if (!selectedDocVersion || !docWithAllVersions) return;

    const activeFileName =
      activeFile?.filename || (isDiffMode ? "changes-file" : "file");
    const organizationName = getOrgFromUser(user, orgId)?.name;
    const downloadFileName = (baseName: string) =>
      buildDownloadFileName({
        baseName,
        deviceName: docWithAllVersions.deviceId ? device?.name : null,
        organizationName,
      });

    if (isDiffMode || selectedDocVersion.revision === null) {
      handleFileDownload(activeBlob, downloadFileName(activeFileName));
      return;
    }

    const archiveFileName = getArchiveFileName(activeFileName);

    try {
      const response = await exportUploadedDocumentArchive({
        documentId: docWithAllVersions.id,
        orgId,
        versionId: selectedDocVersion.id,
      });
      handleFileDownload(response.data, downloadFileName(archiveFileName));
    } catch {
      showSnackbar("Failed to prepare document download. Please try again.");
    }
  };

  return (
    <div className="relative flex w-full flex-1 flex-col">
      {docWithAllVersions && selectedDocVersion && documents && (
        <DocViewerTopBar
          docProps={{
            docs: docInstances,
            selectedDoc: docWithAllVersions,
            selectedVersion: selectedDocVersion,
            hasOutstandingAction: outstandingAction.shouldShow,
            loadingNewDocVersion: isCreatingVersion,
            onDocInfoButtonClick: toggleDrawer,
            onSelectDocVersion: (v) =>
              navigateToDoc(docWithAllVersions.id, v.id),
            isDocEnabled: true,
            onEditClick: () =>
              !isDiffMode
                ? isAVersionBeforeFirstRevision
                  ? replaceFileInput.current?.click()
                  : setShowUploadModal("NEW_CHANGES_AND_NEW_DOC")
                : setShowUploadModal("EDIT_CHANGES_DOC"),
            onSelectDocInstance: (doc) => {
              navigateToDoc(doc.id, doc.versions[0]?.id);
            },
            isDiffMode: isDiffMode,
            onToggleDiff:
              isFirstRevisionOrVersionForFirstRevision === null ||
              isFirstRevisionOrVersionForFirstRevision
                ? undefined
                : () => {
                    // Leave Changes screen if open
                    if (isDiffMode) {
                      setIsDiffMode(false);
                      return;
                    }

                    // If there is no changes document, let them upload one via the modal
                    if (!selectedDocVersion.changesFileId) {
                      setShowUploadModal("NEW_CHANGES_DOC");
                    } else {
                      // If there is a changes document, show the changes document
                      setIsDiffMode(true);
                    }
                  },
          }}
          handleFileDownload={downloadActiveFile}
          onDeleteChangesDocument={() => setShowDeleteConfirmation(true)}
          handleNewRecordFromFile={handleNewRecordFromFile}
          fileInput={fileInput}
        />
      )}
      {docWithAllVersions && documents && templatesConfig && user && (
        <DocumentContextWarnings
          deviceId={deviceId}
          documents={documents}
          orgId={orgId}
          tasks={tasks}
          templateKey={docWithAllVersions.templateKey}
          templatesConfig={templatesConfig}
          user={user}
        />
      )}
      {hasVersions && activeFile && isActiveFileSuccess && activeBlob && (
        <FilePreview file={activeFile} fileBlob={activeBlob} />
      )}
      {(!hasVersions || (selectedDocVersion && !selectedDocVersion.fileId)) && (
        <div data-testid="file-upload-container" className="self-center h-full">
          <FileUpload
            uploadStatus={uploadStatus}
            handleFileChange={handleFileChange}
          />
        </div>
      )}
      {selectedDocVersion && docWithAllVersions && !isDiffMode && (
        <DocInfoDrawer
          selectedDoc={docWithAllVersions}
          isOpen={showDocInfoDrawer}
          onDrawerClose={toggleDrawer}
          selectedDocVersion={selectedDocVersion}
          deviceId={deviceId}
          onBeforeSubmitApproval={handleBeforeSubmitApproval}
        />
      )}
      {showUploadModal && docWithAllVersions && selectedDocVersion && (
        <UploadModal
          type={showUploadModal}
          isOpen={!!showUploadModal}
          onClose={() => setShowUploadModal(undefined)}
          orgId={orgId}
          deviceId={deviceId}
          selectedDoc={docWithAllVersions}
          selectedDocVersion={selectedDocVersion}
          createNewVersion={createNewVersion}
          onVersionCreated={(v) => navigateToDoc(docWithAllVersions.id, v.id)}
        />
      )}
      <ConfirmationModal
        open={showDeleteConfirmation}
        title="Delete Changes Document"
        content="Are you sure you want to delete the changes document? This action cannot be undone."
        confirmText="Delete"
        onAbort={onAbort}
        onConfirm={onConfirmDeleteChangesDocument}
      />
      <input
        type="file"
        className="hidden"
        accept={DEFAULT_ACCEPTED_FILE_TYPES}
        data-testid="hidden-replace-file-input"
        ref={replaceFileInput}
        onChange={handleReplaceFile}
      />
    </div>
  );
};
